import React, { useState } from 'react';
import { Mail, Lock, LogIn, Eye, EyeOff } from 'lucide-react';
import { AppLogo } from '../Layout/AppLogo';
import { ThemeToggle } from '../Layout/ThemeToggle';

interface SimpleLoginPageProps {
  onLogin: (email: string, password: string) => Promise<{ success: boolean; error?: string; message?: string }>;
}

export const SimpleLoginPage: React.FC<SimpleLoginPageProps> = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await onLogin(formData.email.trim(), formData.password);

      if (!result.success) {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo 
      dark:bg-dark-gradient flex items-center justify-center p-4">
      
      <div className="w-full max-w-sm">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl rounded-2xl 
          border border-modular-stone-alloy/30 dark:border-dark-border/50 p-8 shadow-lg">
          
          {/* Theme Toggle */}
          <div className="flex justify-end mb-6">
            <ThemeToggle />
          </div>

          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <AppLogo size="lg" animated={false} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-2">
              PromptOS
            </h1>
            <p className="text-gray-600 dark:text-dark-text-secondary">
              Sign in to your account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                text-gray-400 dark:text-gray-500" />
              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-dark-card border 
                  border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                  focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                  focus:border-transparent transition-all duration-300"
                required
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                text-gray-400 dark:text-gray-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-dark-card border 
                  border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                  focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                  focus:border-transparent transition-all duration-300"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 
                  hover:text-modular-accent-blue dark:hover:text-modular-accent-blue transition-colors duration-300"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 
                rounded-lg p-3 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-modular-primary-gradient text-white font-semibold 
                py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-modular-accent-blue 
                focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-surface 
                transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
                shadow-modular-card hover:shadow-modular-glow transform hover:scale-[1.02] 
                active:scale-[0.98] flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-2" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-gray-600 dark:text-dark-text-secondary text-sm">
              Don't have an account?{' '}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/auth';
                }}
                className="text-modular-accent-blue dark:text-modular-accent-blue hover:text-blue-700 
                  dark:hover:text-blue-300 font-semibold transition-colors duration-300 
                  hover:underline"
              >
                Sign up here
              </button>
            </p>
            
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/auth';
              }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-modular-accent-blue
                dark:hover:text-modular-accent-blue transition-colors duration-300 hover:underline"
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};